import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramFanHorsepower } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.electricalFanHp.v1';

type Fields = { airFlow: string; staticPressure: string; fanEfficiency: string; fanHorsepower: string };
const EMPTY: Fields = { airFlow: '', staticPressure: '', fanEfficiency: '', fanHorsepower: '' };

/**
 * Fan Horsepower — Fan HP = (CFM × SP) / (6356 × Eff)
 * Efficiency entered as percent (e.g. 65 → 0.65).
 */
const ElectricalMotorCalculationsFanHorsepowerScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp, isImperial } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 3) {
      setError('Fill any 3 of the 4 fields to solve for the missing one.');
      return;
    }
    if (filled === 4) {
      setError('All 4 values are filled — clear one to solve for it.');
      return;
    }

    const cfm = toImp(fields.airFlow, 'flow');
    const sp = toImp(fields.staticPressure, 'pressure');
    const eff = parseFloat(fields.fanEfficiency);
    const hp = toImp(fields.fanHorsepower, 'power');

    if (fields.fanHorsepower === '') {
      if (cfm <= 0) { setError('Air flow must be greater than zero.'); return; }
      if (sp < 0) { setError('Static pressure cannot be negative.'); return; }
      if (eff <= 0 || eff > 100) { setError('Fan efficiency must be between 0 and 100%.'); return; }
      setSolvedFor('fanHorsepower');
      set({ fanHorsepower: fromImp((cfm * sp) / (6356 * (eff / 100)), 'power', 3) });
    } else if (fields.airFlow === '') {
      if (sp <= 0) { setError('Static pressure must be greater than zero.'); return; }
      if (eff <= 0 || eff > 100) { setError('Fan efficiency must be between 0 and 100%.'); return; }
      if (hp <= 0) { setError('Fan horsepower must be greater than zero.'); return; }
      setSolvedFor('airFlow');
      set({ airFlow: fromImp((hp * 6356 * (eff / 100)) / sp, 'flow', 0) });
    } else if (fields.staticPressure === '') {
      if (cfm <= 0) { setError('Air flow must be greater than zero.'); return; }
      if (eff <= 0 || eff > 100) { setError('Fan efficiency must be between 0 and 100%.'); return; }
      if (hp <= 0) { setError('Fan horsepower must be greater than zero.'); return; }
      setSolvedFor('staticPressure');
      set({ staticPressure: fromImp((hp * 6356 * (eff / 100)) / cfm, 'pressure', 2) });
    } else if (fields.fanEfficiency === '') {
      if (cfm <= 0) { setError('Air flow must be greater than zero.'); return; }
      if (sp <= 0) { setError('Static pressure must be greater than zero.'); return; }
      if (hp <= 0) { setError('Fan horsepower must be greater than zero.'); return; }
      const effCalc = ((cfm * sp) / (6356 * hp)) * 100;
      if (effCalc > 100) { setError('Calculated efficiency exceeds 100%. Please check your values.'); return; }
      setSolvedFor('fanEfficiency');
      set({ fanEfficiency: effCalc.toFixed(1) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.airFlow && { label: 'Air Flow', value: fields.airFlow, unit: unit('flow'), emphasis: solvedFor === 'airFlow' },
    fields.staticPressure && { label: 'Static Pressure', value: fields.staticPressure, unit: unit('pressure'), emphasis: solvedFor === 'staticPressure' },
    fields.fanEfficiency && { label: 'Fan Efficiency', value: fields.fanEfficiency, unit: '%', emphasis: solvedFor === 'fanEfficiency' },
    fields.fanHorsepower && { label: 'Fan Power', value: fields.fanHorsepower, unit: unit('power'), emphasis: solvedFor === 'fanHorsepower' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Fan Horsepower"
      subtitle="Calculate fan horsepower from air flow, static pressure, and efficiency"
      formula={
        <>
          <FormulaLine>{isImperial ? 'Fan HP = (CFM × SP) / (6356 × Eff)' : 'Fan kW = (L/s × Pa) / (10,000 × Eff)'}</FormulaLine>
          <FormulaNote>Fan Power = {unit('power')}, Flow = {unit('flow')}, SP = {unit('pressure')}, Eff = Fan Efficiency (%)</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter={isImperial ? 'Formula: Fan HP = (CFM × SP) / (6356 × Eff)' : 'Formula: Fan kW = (L/s × Pa) / (10,000 × Eff)'}
      diagram={<DiagramFanHorsepower />}
      outputSlots={{
        cfm: fields.airFlow ? toImp(fields.airFlow, 'flow') : undefined,
        static_pressure_iwg: fields.staticPressure ? toImp(fields.staticPressure, 'pressure') : undefined,
        fan_efficiency_pct: fields.fanEfficiency ? parseFloat(fields.fanEfficiency) : undefined,
        fan_hp: fields.fanHorsepower ? toImp(fields.fanHorsepower, 'power') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.airFlow) {
          patch.airFlow = fromImp(values.cfm, 'flow', 0);
        }
        if (values.static_pressure_iwg != null && !fields.staticPressure) {
          patch.staticPressure = fromImp(values.static_pressure_iwg, 'pressure', 2);
        }
        if (values.fan_efficiency_pct != null && !fields.fanEfficiency) {
          patch.fanEfficiency = values.fan_efficiency_pct.toFixed(1);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Air Flow"
          value={fields.airFlow}
          onChangeText={(v) => set({ airFlow: v })}
          rightText={unit('flow')}
          maxDecimals={0}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Static Pressure"
          value={fields.staticPressure}
          onChangeText={(v) => set({ staticPressure: v })}
          rightText={unit('pressure')}
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Fan Efficiency"
          value={fields.fanEfficiency}
          onChangeText={(v) => set({ fanEfficiency: v })}
          rightText="%"
          maxDecimals={1}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Fan Power (${unit('power')})`}
          value={fields.fanHorsepower}
          onChangeText={(v) => set({ fanHorsepower: v })}
          rightText={unit('power')}
          maxDecimals={3}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default ElectricalMotorCalculationsFanHorsepowerScreen;
